<script lang="ts">
  import { formatDate, formatAmount } from '../../lib/formatters';
  import { Badge } from '../common';

  interface RecurringTransaction {
    pattern: string;
    displayName: string;
    category: string;
    avgAmount: number;
    minAmount: number;
    maxAmount: number;
    avgInterval: number;
    occurrences: number;
    firstDate: string;
    lastDate: string;
    expectedDate: string;
    daysUntilDue: number;
    recurringType: 'subscription' | 'bill';
    confidence: number;
    status: 'not_due' | 'due_soon' | 'due_today' | 'overdue' | 'possibly_ended';
    totalPaid: number;
    averageAmount: number;
    transactionCount: number;
  }

  type Props = {
    items: RecurringTransaction[];
    editingIntervalPattern: string | null;
    editIntervalValue: number;
    showConfidenceTooltip: Record<string, boolean>;
    onStartEditingInterval: (pattern: string, currentInterval: number) => void;
    onCancelEditingInterval: () => void;
    onSaveInterval: (pattern: string) => void;
    onEditIntervalValueChange: (value: number) => void;
    onShowConfidenceTooltip: (pattern: string, show: boolean) => void;
  };

  let {
    items,
    editingIntervalPattern,
    editIntervalValue,
    showConfidenceTooltip,
    onStartEditingInterval,
    onCancelEditingInterval,
    onSaveInterval,
    onEditIntervalValueChange,
    onShowConfidenceTooltip
  }: Props = $props();

  function getStatusVariant(status: string): 'warning' | 'due-soon' | 'overdue' | 'not-due' {
    switch (status) {
      case 'due_today': return 'warning';
      case 'due_soon': return 'due-soon';
      case 'overdue': return 'overdue';
      case 'possibly_ended': return 'warning';
      default: return 'not-due';
    }
  }

  function getTypeVariant(type: string): 'subscription' | 'bill' {
    return type === 'subscription' ? 'subscription' : 'bill';
  }

  function getStatusLabel(item: RecurringTransaction): string {
    if (item.status === 'due_today') return 'Due Today';
    if (item.status === 'possibly_ended') return 'Possibly Cancelled';
    if (item.daysUntilDue > 0) return `Due in ${item.daysUntilDue} day${item.daysUntilDue !== 1 ? 's' : ''}`;
    if (item.status === 'overdue') return `Overdue by ${Math.abs(item.daysUntilDue)} day${Math.abs(item.daysUntilDue) !== 1 ? 's' : ''}`;
    return 'Not due';
  }
</script>

<div class="table-container">
  <table>
    <thead>
      <tr>
        <th>Name</th>
        <th>Type</th>
        <th>Category</th>
        <th>Amount</th>
        <th>Frequency</th>
        <th>Last Paid</th>
        <th>Next Due</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>
      {#each items as item (item.pattern)}
        <tr>
          <td>
            <div class="cell-name">
              {item.displayName}
              {#if item.confidence < 0.8}
                <span
                  class="confidence-indicator"
                  onmouseenter={() => onShowConfidenceTooltip(item.pattern, true)}
                  onmouseleave={() => onShowConfidenceTooltip(item.pattern, false)}
                >
                  <span class="tooltip-icon">⚠️</span>
                  {#if showConfidenceTooltip[item.pattern]}
                    <span class="custom-tooltip">
                      Low confidence detection
                    </span>
                  {/if}
                </span>
              {/if}
            </div>
          </td>
          <td>
            <Badge variant={getTypeVariant(item.recurringType)} size="sm">
              {item.recurringType === 'subscription' ? 'Subscription' : 'Bill'}
            </Badge>
          </td>
          <td>{item.category}</td>
          <td class="amount-cell">${formatAmount(item.avgAmount)}</td>
          <td>
            {#if editingIntervalPattern === item.pattern}
              <div class="interval-edit">
                <input
                  type="number"
                  value={editIntervalValue}
                  oninput={(e) => onEditIntervalValueChange(Number(e.currentTarget.value))}
                  min="1"
                  class="interval-input"
                />
                <span class="days-label">days</span>
                <button class="save-btn-sm" onclick={() => onSaveInterval(item.pattern)}>✓</button>
                <button class="cancel-btn-sm" onclick={onCancelEditingInterval}>✕</button>
              </div>
            {:else}
              <div class="interval-display">
                <span>{item.avgInterval} days</span>
                <button
                  class="edit-interval-btn"
                  onclick={() => onStartEditingInterval(item.pattern, item.avgInterval)}
                  title="Edit frequency"
                >
                  ✏️
                </button>
              </div>
            {/if}
          </td>
          <td>{formatDate(item.lastDate)}</td>
          <td>{formatDate(item.expectedDate)}</td>
          <td>
            <Badge variant={getStatusVariant(item.status)} size="sm">
              {item.status === 'not_due' ? 'Not due' : getStatusLabel(item)}
            </Badge>
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
</div>

<style lang="scss">
  .table-container {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    background: white;
    border-radius: var(--radius-lg);
    overflow: hidden;
    box-shadow: var(--shadow-md);

    thead {
      background: var(--color-background-secondary);

      th {
        padding: var(--spacing-sm) var(--spacing-md);
        text-align: left;
        font-size: var(--font-size-xs);
        font-weight: var(--font-weight-semibold);
        color: var(--color-text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    }

    tbody {
      tr {
        border-top: 1px solid var(--color-border);
        transition: background var(--transition-fast);

        &:hover {
          background: var(--color-background-secondary);
        }

        td {
          padding: var(--spacing-md) 12px;
          font-size: var(--font-size-sm);
          color: var(--color-text-secondary);

          &.amount-cell {
            font-weight: var(--font-weight-semibold);
            text-align: left;
          }
        }
      }
    }
  }

  .cell-name {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    font-weight: var(--font-weight-semibold);

    .confidence-indicator {
      position: relative;
      font-size: var(--font-size-sm);
      opacity: 0.7;
      cursor: help;
      flex-shrink: 0;
      transition: opacity 0.2s;

      &:hover {
        opacity: 1;
      }

      .tooltip-icon {
        display: inline-block;
      }
    }
  }

  .custom-tooltip {
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 8px;
    padding: var(--spacing-sm) 10px;
    background: var(--color-text-primary);
    color: white;
    font-size: var(--font-size-sm);
    border-radius: var(--radius-md);
    white-space: nowrap;
    z-index: var(--z-modal);
    pointer-events: none;
    box-shadow: var(--shadow-lg);

    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-top-color: var(--color-text-primary);
    }
  }

  .interval-display {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
  }

  .edit-interval-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: var(--font-size-sm);
    padding: 2px var(--spacing-xs);
    opacity: 0.5;
    transition: opacity 0.15s;

    &:hover {
      opacity: 1;
    }
  }

  .interval-edit {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
  }

  .interval-input {
    width: 60px;
    padding: var(--spacing-xs) 6px;
    border: 1px solid var(--color-border-medium);
    border-radius: var(--spacing-xs);
    font-size: var(--font-size-sm);

    &:focus {
      outline: none;
      border-color: var(--color-primary-bright);
    }
  }

  .days-label {
    font-size: var(--font-size-sm);
    color: var(--color-text-tertiary);
  }

  .save-btn-sm {
    background: var(--color-success);
    color: white;
    border: none;
    border-radius: var(--spacing-xs);
    padding: var(--spacing-xs) 8px;
    cursor: pointer;
    font-size: var(--font-size-sm);
    transition: background var(--transition-fast);

    &:hover {
      background: var(--color-success-hover);
    }
  }

  .cancel-btn-sm {
    background: var(--color-danger);
    color: white;
    border: none;
    border-radius: var(--spacing-xs);
    padding: var(--spacing-xs) 8px;
    cursor: pointer;
    font-size: var(--font-size-sm);
    transition: background var(--transition-fast);

    &:hover {
      background: var(--color-danger-hover);
    }
  }
</style>
